import type { AdminUserListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { UsersFilterBar } from './_components/UsersFilterBar';
import { buildUserColumns } from './_lib/userColumns';
import { hasActiveFilters, userFiltersSchema } from './_lib/userFilters';
import { UserDetailDrawer } from './UserDetailDrawer';
import { useAdminUsersList } from '../../api/admin-users';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import { Routes, userDetailPath } from '../../routes/routes';

/**
 * `/users` — users moderation list (AUT-124 / ADMIN-12).
 *
 * Gated on `manage_users` (the controller-level permission). PII columns
 * (phone / email / name) render via `<RedactedField perm="view_pii">`,
 * which agrees with the server's `piiRevealed` flag (both derive from the
 * same backend permission post-substep-0).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. Row click opens the 720px detail drawer.
 *
 * The open user lives in the path (`/users/:id`, AUT-542 / architect B1), so
 * a link to a person from another section opens the drawer directly; closing
 * returns to `/users`. Both keep the query string (filters).
 */
const UsersListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(userFiltersSchema);
  const pager = useCursorPagination();
  const navigate = useNavigate();
  const { search } = useLocation();
  const selectedId = useMatch(Routes.App.UserDetail)?.params.id ?? null;

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${filters.role ?? ''}|${(filters.status ?? []).join(',')}|${filters.kycStatus ?? ''}|${filters.q ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminUsersList({
    ...(filters.role ? { role: filters.role } : {}),
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.kycStatus ? { kycStatus: filters.kycStatus } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildUserColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('users.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('users.subtitle')}
        </Typography>
      </Box>

      <UsersFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminUserListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => navigate({ pathname: userDetailPath(row.id), search })}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('users.empty')}
          </Typography>
        }
      />

      <UserDetailDrawer
        userId={selectedId}
        onClose={() => navigate({ pathname: Routes.App.Users, search })}
      />
    </Box>
  );
};

const Observed = observer(UsersListPageInner);

/** Whole-page gate: no `manage_users` → the default 403 panel. */
export const UsersListPage = (): ReactElement => (
  <RequirePermission perm="manage_users">
    <Observed />
  </RequirePermission>
);
